/**
 * Presentation helpers for the manager's approval queue and the public
 * "request sent" confirmation (TEN-06). Pure functions so the copy and the
 * deadline maths are pinned by tests rather than by reading JSX.
 */

/** Mirrors `REGISTRATION_LAPSE_DAYS` in api/src/tenant_registrations.ts. */
export const REGISTRATION_LAPSE_DAYS = 10
const DAY_MS = 86_400_000

/** At or below this many whole days left, the queue row turns urgent. */
export const LAPSE_URGENT_DAYS = 3

export interface LapseInfo {
  /** Whole days left before the request lapses, never negative. */
  daysLeft: number
  urgent: boolean
  label: string
}

/**
 * When an unanswered request lapses. The server only ages a pending request
 * that carries a `request_registered` event (older rows are deliberately never
 * lapsed), so a row without one returns null and shows no deadline at all
 * rather than a countdown the server will never honour.
 */
export function lapseInfo(
  reg: { createdAt: string; events?: { kind: string }[] },
  now = Date.now(),
): LapseInfo | null {
  if (reg.events?.some(e => e.kind === 'request_registered') !== true) return null
  const created = Date.parse(reg.createdAt)
  if (!Number.isFinite(created)) return null
  const msLeft = created + REGISTRATION_LAPSE_DAYS * DAY_MS - now
  const daysLeft = Math.max(0, Math.ceil(msLeft / DAY_MS))
  const urgent = daysLeft <= LAPSE_URGENT_DAYS
  const label = msLeft <= 0
    ? 'Lapses today'
    : daysLeft === 1 ? 'Lapses in 1 day' : `Lapses in ${daysLeft} days`
  return { daysLeft, urgent, label }
}

/** Every submitted plate with its state when known, primary plate first. */
export function queuePlates(reg: {
  plate: string
  plates?: string[]
  plateStates?: { plate: string; state: string }[]
}): { plate: string; state?: string }[] {
  const all = [reg.plate, ...(reg.plates ?? [])].filter(Boolean)
  const seen = new Set<string>()
  const states = new Map((reg.plateStates ?? []).map(v => [v.plate, v.state]))
  return all
    .filter(plate => (seen.has(plate) ? false : (seen.add(plate), true)))
    .map(plate => {
      const state = states.get(plate)
      return state ? { plate, state } : { plate }
    })
}

/**
 * What the applicant is told after submitting. Approval and decline notices go
 * out by EMAIL only (api/src/notifications/registration.ts); nothing is ever
 * texted. So the promise is made only to someone who gave an email, and a
 * phone-only applicant is told to ask their manager. Actual SMS decision
 * notices are a separate notification contract; do not promise one here.
 */
export function requestSentCopy(business: string, hasEmail: boolean): string {
  return hasEmail
    ? `${business} needs to approve you before you can park. We'll email you once they decide. There's nothing to pay and nothing to display on the dash.`
    : `${business} needs to approve you before you can park. We won't text or email you, so check with your manager before you park. There's nothing to pay and nothing to display on the dash.`
}
